import { useMemo, useState } from "react"
import { Box, Stack, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"

import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { useEnvironment } from "../../../../environment/envHooks"
import { PageHeader } from "../../../../../shared/components/pageHeader/PageHeader"
import { Page } from "../../../../../shared/components/pageHeader/PageHeaderApi"
import { ComingSoonDialog } from "../../../../../shared/components/dialogs/ComingSoonDialog"
import { ConfirmModal } from "../../../../../shared/components/dialogs/ConfirmModal"
import { StandardSquareFilter } from "../../../../../shared/components/filters/squareFilter/StandardSquareFilter"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../shared/components/links/StandardBreadcrumbs"
import {
    getPublicCanteenMenuBridgeRoute,
    getPublicMenuDisplayBridgeRoute,
    MERCHANT_ADMIN_ROUTE,
    MERCHANT_PRODUCTION_MEETING_SERVICE_VIEW_ROUTE,
} from "../../../../../constants/routes"
import { useApiMutation } from "../../../../../core/api/useApiMutation"
import { fetchOneTimeToken } from "../../../../login/loginApi"
import { PRODUCTION_VIEW_FULLSCREEN_PARAM } from "../../../merchant/production/meetingService/productionView/helpers"

import type { InfoDisplay } from "../infoDisplaysTypes"
import { InfoDisplayFilter, InfoDisplayType } from "../infoDisplaysTypes"
import { DisplayLinkDialog } from "../components/DisplayLinkDialog"
import { InfoDisplayCard } from "../components/InfoDisplayCard"

const getEmbedCode = (publicUrl: string) =>
    `<iframe src="${publicUrl}" width="1920" height="1080" frameborder="0" allowfullscreen></iframe>`

const useStyles = makeStyles()((theme) => ({
    pageSubtitle: {
        maxWidth: "38rem",
        marginBottom: "1.5rem",
        color: theme.palette.tailwind.slate[500],
    },
    toolbar: {
        paddingBottom: "1.125rem",
        marginBottom: "1.5rem",
        borderBottom: `1px solid ${theme.palette.tailwind.slate[200]}`,
    },
    countLabel: {
        color: theme.palette.tailwind.slate[400],
    },
    grid: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(18.5rem, 21.5rem))",
        justifyContent: "start",
        gap: "1.375rem",
    },
}))

// TODO: replace with the real display uid from the displays API.
// The device secret created by hand on the test environment, so the links work there; the
// page itself is registered outside production (router + nav are MatchEnvironment-gated).
// Both menu displays share it: the secret logs the screen into a device session whose merchant
// then drives the data, regardless of which display page the link opens.
const INFO_DISPLAY_DEVICE_SECRET = "SECRET_KEY_1000"

export function MerchantAdminInfoDisplays() {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()
    const { currentEnv } = useEnvironment()

    const { mutateAsync: createOneTimeToken } = useApiMutation(fetchOneTimeToken, {})

    const [filter, setFilter] = useState(InfoDisplayFilter.ALL)
    const [linkDisplay, setLinkDisplay] = useState<InfoDisplay | null>(null)
    const [resetDisplay, setResetDisplay] = useState<InfoDisplay | null>(null)
    const [isComingSoonOpen, setComingSoonOpen] = useState(false)

    // TODO: replace the hardcoded displays with the displays API once it exists.
    const displays: ReadonlyArray<InfoDisplay> = useMemo(() => {
        const baseUrl = currentEnv.webapp?.baseUrl ?? ""
        const menuPublicUrl = `${baseUrl}${getPublicCanteenMenuBridgeRoute(INFO_DISPLAY_DEVICE_SECRET)}`
        return [
            {
                id: "menu-simple",
                type: InfoDisplayType.MENU,
                nameId: "merchant-info-displays-card-menu-simple-name",
                nameFallback: "Week menu — Simple (iFrame)",
                descriptionId: "merchant-info-displays-card-menu-simple-description",
                descriptionFallback: "Used to show menu on website / intranet.",
                publicUrl: menuPublicUrl,
                isEmbeddable: true,
                linkDialogTextsId: "merchant-info-displays-link-dialog-embed",
            },
            {
                id: "menu-layout-1",
                type: InfoDisplayType.MENU,
                nameId: "merchant-info-displays-card-menu-layout-name",
                nameFallback: "Today's menu — Layout 1",
                descriptionId: "merchant-info-displays-card-menu-layout-description",
                descriptionFallback: "Used to display on screen in canteen.",
                // The link carries the device secret and logs the screen into its device session
                // via the bridge, so preview and share open exactly the same screen.
                publicUrl: `${baseUrl}${getPublicMenuDisplayBridgeRoute(INFO_DISPLAY_DEVICE_SECRET)}`,
                isEmbeddable: false,
                linkDialogTextsId: "merchant-info-displays-link-dialog-permalink",
            },
            {
                id: "production",
                type: InfoDisplayType.PRODUCTION,
                nameId: "merchant-info-displays-card-production-name",
                nameFallback: "Production screen",
                descriptionId: "merchant-info-displays-card-production-description",
                descriptionFallback: "Used to display on screen in the production areas to manage upcoming orders.",
                // The production view exposes high-risk order actions, so its link must require a real
                // user login - no low-privilege device token, unlike the menu displays. After the login
                // it opens straight in the locked fullscreen view, like a screen should.
                publicUrl: `${baseUrl}${MERCHANT_PRODUCTION_MEETING_SERVICE_VIEW_ROUTE}?${PRODUCTION_VIEW_FULLSCREEN_PARAM}=true`,
                isEmbeddable: false,
                linkDialogTextsId: "merchant-info-displays-link-dialog-permalink-login-required",
            },
        ]
    }, [currentEnv])

    const visibleDisplays = displays.filter(
        (display) => filter === InfoDisplayFilter.ALL || String(display.type) === String(filter),
    )

    // The preview opens the very link that is shared, so what the admin sees is what the screen gets.
    const handlePreview = async (display: InfoDisplay) => {
        if (display.type !== InfoDisplayType.PRODUCTION) {
            window.open(display.publicUrl, "_blank")
            return
        }
        // The production link requires a real login, so the preview adds a fresh one-time token,
        // which the auth guard turns into an auto-login when the tab has no session. The tab
        // opens before the request, otherwise the browser would treat the delayed window.open
        // as a popup and block it.
        const previewTab = window.open("", "_blank")
        try {
            const { token } = await createOneTimeToken({})
            const previewUrl = `${display.publicUrl}&otp=${encodeURIComponent(token)}`
            if (previewTab) previewTab.location.href = previewUrl
            else window.open(previewUrl, "_blank")
        } catch {
            // The mutation already reports the error in a snackbar; the blank tab just closes.
            previewTab?.close()
        }
    }

    // TODO: call the reset display uid API here once it exists.
    const handleResetConfirm = () => {
        setResetDisplay(null)
        setLinkDisplay(null)
        setComingSoonOpen(true)
    }

    return (
        <>
            <LocalizedStrict id="merchant-info-displays-header" attrs={{ title: true }}>
                <PageHeader page={Page.INFO_DISPLAYS} title="Integration feeds">
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="merchant-nav-item-home">
                            <BreadcrumbLink to={MERCHANT_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-nav-item-communication-header">
                            <Breadcrumb>Communication</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-nav-item-info-displays">
                            <Breadcrumb>Integration feeds</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            <LocalizedStrict id="merchant-info-displays-page-subtitle">
                <Typography variant="body2" className={classes.pageSubtitle}>
                    Screens published from Facilitynet. Preview a display, share it as a link or embed, or reset its
                    link to revoke access.
                </Typography>
            </LocalizedStrict>

            <Stack direction="row" alignItems="center" spacing=".875rem" className={classes.toolbar}>
                <StandardSquareFilter
                    value={filter}
                    options={Object.values(InfoDisplayFilter)}
                    translationBase="merchant-info-displays-filter"
                    onChange={setFilter}
                />
                <LocalizedStrict id="merchant-info-displays-count-label" vars={{ count: visibleDisplays.length }}>
                    <Typography variant="body2" className={classes.countLabel}>
                        {visibleDisplays.length} displays
                    </Typography>
                </LocalizedStrict>
            </Stack>

            <Box className={classes.grid}>
                {visibleDisplays.map((display) => (
                    <InfoDisplayCard
                        key={display.id}
                        display={display}
                        // TODO: wire to a real display activate/deactivate endpoint once it exists;
                        // the backend has none yet, so the action only reports coming soon.
                        isInactive={false}
                        onPreview={() => handlePreview(display)}
                        onOpenLink={() => setLinkDisplay(display)}
                        onResetLink={() => setResetDisplay(display)}
                        onToggleInactive={() => setComingSoonOpen(true)}
                    />
                ))}
            </Box>

            {linkDisplay && (
                <LocalizedStrict
                    id={linkDisplay.linkDialogTextsId}
                    attrs={{ dialogTitle: true, subtitle: true }}
                    vars={{ name: l10n.getString(linkDisplay.nameId) }}
                >
                    <DisplayLinkDialog
                        open
                        dialogTitle="Permalink"
                        subtitle="Anyone with this link can view the display."
                        permalink={linkDisplay.publicUrl}
                        embedCode={linkDisplay.isEmbeddable ? getEmbedCode(linkDisplay.publicUrl) : undefined}
                        onClose={() => setLinkDisplay(null)}
                        onResetLink={() => setResetDisplay(linkDisplay)}
                    />
                </LocalizedStrict>
            )}
            {resetDisplay && (
                <LocalizedStrict
                    id="merchant-info-displays-reset-dialog"
                    attrs={{ title: true, message: true, buttonLabelSubmit: true }}
                    vars={{ name: l10n.getString(resetDisplay.nameId) }}
                >
                    <ConfirmModal
                        open
                        title="Reset link?"
                        message=""
                        buttonLabelSubmit="Reset link"
                        handleClick={handleResetConfirm}
                        onClose={() => setResetDisplay(null)}
                    />
                </LocalizedStrict>
            )}
            {isComingSoonOpen && <ComingSoonDialog open onClose={() => setComingSoonOpen(false)} />}
        </>
    )
}
